/**
 * AccessibilityMenu — UI scale, colorblind modes, high contrast, text size.
 *
 * Accessible from /accesibilidad command or Options menu.
 * Settings persist in localStorage.
 */

const STORAGE_KEY = 'fedeworld_accessibility';

const COLORBLIND_FILTERS = {
  none:        { label: 'Ninguno',        filter: '' },
  protanopia:  { label: 'Protanopía',     filter: 'url(#cb-protanopia)' },
  deuteranopia:{ label: 'Deuteranopía',   filter: 'url(#cb-deuteranopia)' },
  tritanopia:  { label: 'Tritanopía',     filter: 'url(#cb-tritanopia)' },
};

export class AccessibilityMenu {
  constructor() {
    this.settings = this._load();
    this._injectSVGFilters();
    this._applySettings();
    this._buildUI();
  }

  _load() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      return {
        uiScale: saved.uiScale ?? 1.0,
        colorblind: saved.colorblind ?? 'none',
        highContrast: saved.highContrast ?? false,
        largeText: saved.largeText ?? false,
        reduceMotion: saved.reduceMotion ?? false,
      };
    } catch {
      return { uiScale: 1.0, colorblind: 'none', highContrast: false, largeText: false, reduceMotion: false };
    }
  }

  _save() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
  }

  _injectSVGFilters() {
    if (document.getElementById('fw-cb-filters')) return;
    // SVG color matrix filters for colorblind simulation
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.id = 'fw-cb-filters';
    svg.style.cssText = 'position:absolute;width:0;height:0;';
    svg.innerHTML = `
      <defs>
        <filter id="cb-protanopia">
          <feColorMatrix values="0.567 0.433 0 0 0  0.558 0.442 0 0 0  0 0.242 0.758 0 0  0 0 0 1 0"/>
        </filter>
        <filter id="cb-deuteranopia">
          <feColorMatrix values="0.625 0.375 0 0 0  0.7 0.3 0 0 0  0 0.3 0.7 0 0  0 0 0 1 0"/>
        </filter>
        <filter id="cb-tritanopia">
          <feColorMatrix values="0.95 0.05 0 0 0  0 0.433 0.567 0 0  0 0.475 0.525 0 0  0 0 0 1 0"/>
        </filter>
      </defs>
    `;
    document.body.appendChild(svg);
  }

  _applySettings() {
    // UI scale
    document.body.style.setProperty('--fw-ui-scale', this.settings.uiScale);

    // Colorblind filter
    const filter = COLORBLIND_FILTERS[this.settings.colorblind]?.filter || '';
    document.body.style.filter = filter;

    // High contrast
    if (this.settings.highContrast) {
      document.body.classList.add('fw-high-contrast');
      if (!document.getElementById('fw-hc-style')) {
        const st = document.createElement('style');
        st.id = 'fw-hc-style';
        st.textContent = `
          .fw-high-contrast * { text-shadow: 1px 1px 0 #000 !important; }
          .fw-high-contrast input { border: 2px solid #FFF !important; }
        `;
        document.head.appendChild(st);
      }
    } else {
      document.body.classList.remove('fw-high-contrast');
    }

    // Large text
    const scale = this.settings.largeText ? 1.3 : 1.0;
    document.body.style.fontSize = (13 * scale) + 'px';

    // Reduce motion
    if (this.settings.reduceMotion) {
      if (!document.getElementById('fw-rm-style')) {
        const st = document.createElement('style');
        st.id = 'fw-rm-style';
        st.textContent = `
          *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
        `;
        document.head.appendChild(st);
      }
    } else {
      const st = document.getElementById('fw-rm-style');
      if (st) st.remove();
    }
  }

  _buildUI() {
    this._overlay = document.createElement('div');
    this._overlay.style.cssText = `
      position:fixed; top:0; left:0; width:100%; height:100%;
      background:rgba(0,0,0,0.78); z-index:918; display:none;
      justify-content:center; align-items:center;
    `;

    const panel = document.createElement('div');
    panel.style.cssText = `
      background:#2A2A2A; border:4px solid #4A4A4A;
      padding:24px; min-width:420px;
      font-family:'Courier New',monospace; color:#DDD;
    `;

    const title = document.createElement('div');
    title.textContent = '♿ Accesibilidad';
    title.style.cssText = 'font-size:20px;font-weight:bold;color:#FFD700;margin-bottom:20px;text-align:center;';
    panel.appendChild(title);

    // UI Scale
    panel.appendChild(this._buildSlider('Escala de UI', 'uiScale', 0.8, 1.5, 0.05));

    // Colorblind
    panel.appendChild(this._buildSelect('Daltonismo', 'colorblind',
      Object.entries(COLORBLIND_FILTERS).map(([k, v]) => ({ value: k, label: v.label }))));

    // Toggles
    panel.appendChild(this._buildToggle('Alto Contraste', 'highContrast'));
    panel.appendChild(this._buildToggle('Texto Grande', 'largeText'));
    panel.appendChild(this._buildToggle('Reducir Animaciones', 'reduceMotion'));

    // Close
    const closeBtn = document.createElement('div');
    closeBtn.textContent = 'Cerrar';
    closeBtn.style.cssText = `
      margin-top:16px; padding:10px 0; text-align:center; cursor:pointer;
      background:#555; border:2px solid; border-color:#777 #333 #333 #777;
      font-size:14px; font-weight:bold;
    `;
    closeBtn.addEventListener('click', () => this.hide());
    panel.appendChild(closeBtn);

    this._overlay.appendChild(panel);
    document.body.appendChild(this._overlay);
  }

  _buildSlider(label, key, min, max, step) {
    const row = document.createElement('div');
    row.style.cssText = 'margin-bottom:14px;';
    row.innerHTML = `
      <label style="display:block;margin-bottom:4px;font-size:13px;">${label}: <span class="val">${this.settings[key]}</span></label>
      <input type="range" min="${min}" max="${max}" step="${step}" value="${this.settings[key]}"
        style="width:100%;">
    `;
    const input = row.querySelector('input');
    const val = row.querySelector('.val');
    input.addEventListener('input', () => {
      this.settings[key] = parseFloat(input.value);
      val.textContent = this.settings[key];
      this._applySettings();
      this._save();
    });
    return row;
  }

  _buildSelect(label, key, options) {
    const row = document.createElement('div');
    row.style.cssText = 'margin-bottom:14px;';
    const sel = document.createElement('select');
    sel.style.cssText = 'width:100%;padding:6px;background:#3A3A3A;color:#FFF;border:1px solid #555;font-family:inherit;';
    for (const opt of options) {
      const o = document.createElement('option');
      o.value = opt.value;
      o.textContent = opt.label;
      if (opt.value === this.settings[key]) o.selected = true;
      sel.appendChild(o);
    }
    sel.addEventListener('change', () => {
      this.settings[key] = sel.value;
      this._applySettings();
      this._save();
    });
    row.innerHTML = `<label style="display:block;margin-bottom:4px;font-size:13px;">${label}:</label>`;
    row.appendChild(sel);
    return row;
  }

  _buildToggle(label, key) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;margin-bottom:10px;cursor:pointer;';
    row.innerHTML = `
      <input type="checkbox" style="margin-right:8px;" ${this.settings[key] ? 'checked' : ''}>
      <span style="font-size:13px;">${label}</span>
    `;
    const input = row.querySelector('input');
    input.addEventListener('change', () => {
      this.settings[key] = input.checked;
      this._applySettings();
      this._save();
    });
    return row;
  }

  show() { this._overlay.style.display = 'flex'; this.isOpen = true; }
  hide() { this._overlay.style.display = 'none'; this.isOpen = false; }
}
